import { openDB, type IDBPDatabase } from 'idb'
import type { Product, CartItem, Receipt } from '../types'
import { PREBUILT_PRODUCTS } from '../data/prebuiltCatalog'

const DB_NAME = 'kids-grocery-db'
const DB_VERSION = 1

let dbPromise: Promise<IDBPDatabase<unknown>> | null = null

function getDB() {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(database) {
        if (!database.objectStoreNames.contains('products')) {
          const s = database.createObjectStore('products', { keyPath: 'id' })
          s.createIndex('qrCodeValue', 'qrCodeValue', { unique: true })
        }
        if (!database.objectStoreNames.contains('cart')) {
          database.createObjectStore('cart', { keyPath: 'productId' })
        }
        if (!database.objectStoreNames.contains('receipts')) {
          database.createObjectStore('receipts', { keyPath: 'id' })
        }
        if (!database.objectStoreNames.contains('meta')) {
          database.createObjectStore('meta')
        }
      },
    })
  }
  return dbPromise
}

async function ensurePrebuilt(): Promise<void> {
  const database = await getDB()
  const seeded = await database.get('meta', 'prebuiltSeeded')
  if (seeded) return
  const productTx = database.transaction('products', 'readwrite')
  const store = productTx.objectStore('products')
  for (const p of PREBUILT_PRODUCTS) {
    store.put(p)
  }
  await productTx.done
  await database.put('meta', true, 'prebuiltSeeded')
}

export async function initDb(): Promise<void> {
  await getDB()
  await ensurePrebuilt()
}

export async function getAllProducts(): Promise<Product[]> {
  await ensurePrebuilt()
  const db = await getDB()
  const list = await db.getAll('products')
  return (list as Product[]).sort((a, b) => a.name.localeCompare(b.name))
}

export async function getProductById(id: string): Promise<Product | undefined> {
  const db = await getDB()
  return (await db.get('products', id)) as Product | undefined
}

export async function getProductByQr(qrCodeValue: string): Promise<Product | undefined> {
  const db = await getDB()
  return (await db.getFromIndex('products', 'qrCodeValue', qrCodeValue)) as Product | undefined
}

export async function addProduct(product: Product): Promise<void> {
  const db = await getDB()
  await db.put('products', product)
}

export async function updateProduct(product: Product): Promise<void> {
  await addProduct(product)
}

export async function deleteProduct(id: string): Promise<void> {
  const db = await getDB()
  await db.delete('products', id)
}

export async function getCart(): Promise<CartItem[]> {
  const db = await getDB()
  const list = await db.getAll('cart')
  return (list as CartItem[]).filter((c) => c.quantity > 0)
}

export async function setCart(items: CartItem[]): Promise<void> {
  const db = await getDB()
  const tx = db.transaction('cart', 'readwrite')
  const store = tx.objectStore('cart')
  await store.clear()
  for (const item of items) {
    if (item.quantity > 0) await store.put(item)
  }
  await tx.done
}

export async function addToCart(productId: string, quantity: number, subtotal: number): Promise<void> {
  const cart = await getCart()
  const existing = cart.find((c) => c.productId === productId)
  const rest = cart.filter((c) => c.productId !== productId)
  const newQty = (existing?.quantity ?? 0) + quantity
  const newSub = (existing?.subtotal ?? 0) + subtotal
  if (newQty > 0) rest.push({ productId, quantity: newQty, subtotal: newSub })
  await setCart(rest)
}

export async function removeFromCart(productId: string): Promise<void> {
  const cart = await getCart()
  await setCart(cart.filter((c) => c.productId !== productId))
}

export async function clearCart(): Promise<void> {
  await setCart([])
}

export async function addReceipt(receipt: Receipt): Promise<void> {
  const db = await getDB()
  await db.put('receipts', receipt)
}

export async function getAllReceipts(): Promise<Receipt[]> {
  const db = await getDB()
  const list = await db.getAll('receipts')
  return (list as Receipt[]).sort((a, b) => b.timestamp - a.timestamp)
}

export async function getSetting<K extends string>(key: K): Promise<string | undefined> {
  const db = await getDB()
  return (await db.get('meta', key)) as string | undefined
}

export async function setSetting(key: string, value: string): Promise<void> {
  const db = await getDB()
  await db.put('meta', value, key)
}
